import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createScopedLogger } from "../../../common";
import { formatString } from "../../../common/formatString";
import { useErrorHandler } from "../../../common/hooks";
import { VariableNames } from "../../Constants/VariableNames";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import { InputSelectModel, InputSelectVariant } from "../../Models/InputSelectModel";
import { StackContainerDirection } from "../../Models/StackContainerDirection";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("InputSelectComponent");

const InputSelectComponent: React.FC<IComponentProps<InputSelectModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const [value, setValue] = useState<any>(null);
  const [originalValue, setOriginalValue] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<any[]>([]);
  const [optionsPending, setOptionsPending] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedInitialOptionsRef = useRef<boolean>(false);

  const componentLayoutSx = getComponentLayout(component);

  const handleLoadItems = async (searchText?: string) => {
    if (!component.Items) {
      logger.warn("GetItems expression is not defined.");
      return;
    }

    setOptionsPending(true);
    try {
      const eventContext = context.CreateChild();
      eventContext.VariableScope.set(VariableNames.Event, { text: searchText || "" });

      const result = await eventContext.Evaluator.evaluate(component.Items, eventContext);

      if (result === undefined || result === null) {
        setOptions([]);
        return;
      }

      if (!Array.isArray(result)) {
        logger.error("GetItems expression did not return an array:", result);
        throw new Error("GetItems expression did not return an array: " + result);
      }

      logger.debug("Loaded options:", result);
      setOptions(result);
    } catch (error) {
      errorHandler(error);
      setOptions([]);
    } finally {
      setOptionsPending(false);
    }
  };

  // Load options when input text changes (with debounce after the initial load)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!hasLoadedInitialOptionsRef.current) {
      hasLoadedInitialOptionsRef.current = true;
      handleLoadItems(inputValue);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      handleLoadItems(inputValue);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [component.Items, inputValue]);

  // Load initial value from component.Item on first render
  useEffect(() => {
    const loadInitialValue = async () => {
      if (!component.Value) return;

      try {
        let result = await context.Evaluator.evaluate(component.Value, context);

        if (result === undefined && component.DefaultValue) {
          result = await context.Evaluator.evaluate(component.DefaultValue, context);
          logger.debug("Loading default value for component", result);

          context.Evaluator.assign(component.Value, context, result);
        }

        setValue(result);
        setOriginalValue(result);
      } catch (error) {
        errorHandler(error);
      }
    };

    loadInitialValue();
  }, [component.Value, component.DefaultValue]);

  const getItemKey = useCallback(
    (item: any) => {
      const keyField = component.ItemKeyField || "Id";
      return item[keyField];
    },
    [component.ItemKeyField]
  );

  const getSelectionKeys = useCallback(
    (selectedValue: any) => {
      if (!Array.isArray(selectedValue)) {
        if (selectedValue === null || selectedValue === undefined) {
          return [];
        }

        return [typeof selectedValue === "object" ? getItemKey(selectedValue) : selectedValue];
      }

      return selectedValue
        .map((item: any) => (item !== null && typeof item === "object" ? getItemKey(item) : item))
        .filter((key: any) => key !== null && key !== undefined);
    },
    [getItemKey]
  );

  const commitSelection = useCallback(
    async (
      newValue: any,
      options: {
        evaluateItemSelected?: boolean;
        updateOriginalValue?: boolean;
      } = {}
    ) => {
      const { evaluateItemSelected = true, updateOriginalValue = false } = options;

      setValue(newValue);
      setIsUpdating(true);

      try {
        if (evaluateItemSelected && component.ItemSelected) {
          const eventContext = context.CreateChild();
          eventContext.VariableScope.set(VariableNames.Event, newValue);

          await eventContext.Evaluator.evaluate(component.ItemSelected, eventContext);
        }

        if (component.Value) {
          context.Evaluator.assign(component.Value, context, newValue);
        }

        if (component.IsMultiSelect && component.Values) {
          context.Evaluator.assign(component.Values, context, getSelectionKeys(newValue));
        }

        if (updateOriginalValue) {
          setOriginalValue(newValue);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [component, context, errorHandler, getSelectionKeys]
  );

  const handleChange = (_event: any, newValue: any) => {
    if (component.ItemSelected) {
      setValue(newValue);
    } else {
      commitSelection(newValue, { evaluateItemSelected: false, updateOriginalValue: true });
    }

    if (newValue === null || newValue === undefined || (Array.isArray(newValue) && newValue.length === 0)) {
      setInputValue("");
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleOpen = () => {
    // When dropdown opens, clear input text and load options with empty search
    setInputValue("");
    if (options.length === 0 && !optionsPending) {
      handleLoadItems("");
    }
  };

  const handleSave = useCallback(async () => {
    if (!component.ItemSelected || value === null || value === undefined) return;

    await commitSelection(value, { evaluateItemSelected: true, updateOriginalValue: true });
  }, [value, component.ItemSelected, commitSelection]);

  const handleCancel = useCallback(() => {
    setValue(originalValue);
  }, [originalValue]);

  const hasChanged = value !== originalValue && (value !== null || originalValue !== null);

  const selectionProps = useComponentSelection({ component, componentPath });

  const { isVisible, hiddenSx } = useComponentVisibility(component, context, "InputSelectComponent");

  const handleButtonGroupChange = async (_: React.MouseEvent<HTMLElement> | null, newValue: any) => {
    await commitSelection(newValue, { evaluateItemSelected: true, updateOriginalValue: true });
  };

  const buttonGroupValue = component.IsMultiSelect ? (Array.isArray(value) ? value : value != null ? [value] : []) : (value ?? null);
  const selectValue = component.IsMultiSelect ? (Array.isArray(value) ? value : value != null ? [value] : []) : (value ?? "");

  const getItemLabel = useCallback(
    (item: any) => {
      const itemLabel = component.ItemLabel || "{Label}";
      return formatString(itemLabel, item);
    },
    [component.ItemLabel]
  );

  if (!isVisible) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ ...componentLayoutSx, ...hiddenSx }} paddingTop={1} paddingBottom={1} {...selectionProps}>
      {component.SelectVariant === InputSelectVariant.ButtonGroup ? (
        <FormControl>
          {component.Label && <FormLabel sx={{ mb: 0.5, fontSize: "0.75rem" }}>{component.Label}</FormLabel>}
          <ToggleButtonGroup
            value={buttonGroupValue}
            exclusive={!component.IsMultiSelect}
            onChange={handleButtonGroupChange}
            size="small"
            color="primary"
            disabled={isUpdating}
            orientation={component.SelectDirection === StackContainerDirection.Column ? "vertical" : "horizontal"}
          >
            {options.map((item) => (
              <ToggleButton
                key={getItemKey(item)}
                value={getItemKey(item)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                }}
              >
                {getItemLabel(item)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </FormControl>
      ) : component.SelectVariant === InputSelectVariant.RadioGroup ? (
        <FormControl disabled={isUpdating}>
          {component.Label && <FormLabel>{component.Label}</FormLabel>}
          <RadioGroup
            value={value ?? ""}
            onChange={(_, newValue) => handleButtonGroupChange(null as any, newValue)}
            row={component.SelectDirection !== StackContainerDirection.Column}
          >
            {options.map((item) => (
              <FormControlLabel key={getItemKey(item)} value={getItemKey(item)} control={<Radio size="small" />} label={getItemLabel(item)} />
            ))}
          </RadioGroup>
        </FormControl>
      ) : component.Typeahead ? (
        <Autocomplete
          value={value}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onOpen={handleOpen}
          options={options}
          fullWidth
          size="small"
          getOptionKey={getItemKey}
          getOptionLabel={getItemLabel}
          loading={optionsPending}
          disabled={isUpdating}
          renderInput={(params) => <TextField {...params} label={component.Label} />}
          sx={{ flex: 1 }}
        />
      ) : (
        <FormControl variant="standard" fullWidth sx={{ flex: 1 }} required={component.IsRequired}>
          <InputLabel required={component.IsRequired}>{component.Label}</InputLabel>
          <Select
            value={selectValue}
            defaultValue={selectValue}
            onChange={(event) => handleChange(event, event.target.value)}
            label={component.Label}
            multiple={component.IsMultiSelect}
            disabled={isUpdating || optionsPending}
          >
            {options.map((item) => (
              <MenuItem key={getItemKey(item)} value={getItemKey(item)}>
                {getItemLabel(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {component.SelectVariant !== InputSelectVariant.ButtonGroup &&
        component.SelectVariant !== InputSelectVariant.RadioGroup &&
        component.ItemSelected &&
        hasChanged && (
          <>
            <IconButton onClick={handleSave} color="primary" disabled={isUpdating} loading={isUpdating} size="small">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleCancel} disabled={isUpdating} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
    </Stack>
  );
};

export default InputSelectComponent;
